'use client';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, MenuIcon, XIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const navigation = [
  { name: 'About Us', href: '/about-us/' },
  {
    name: 'Products',
    href: null,
    submenu: [
      {
        name: 'Pancake Coil',
        href: '/products/pancake-coil',
      },
      {
        name: 'Straight Tube',
        href: '/products/straight-tube',
      },
      {
        name: 'Inner Grooved Copper Tubes',
        href: '/products/inner-grooved',
      },
      {
        name: 'Level Wound Coil',
        href: '/products/level-wound-coil',
      },
      {
        name: 'Copper Nickel Tubes',
        href: '/products/copper-nickel-tubes',
      },
      {
        name: 'Aluminium Finstock',
        href: '/products/aluminium-finstock',
      },
      // {
      //   name: 'Straight Bold Tube',
      //   href: '/products/straight-bold-tube',
      // },
    ],
  },
  {
    name: 'Applications',
    href: '/applications',
  },
  { name: 'Quality Control', href: '/quality-control/' },
  { name: 'Contact Us', href: '/contact-us/' },
];

const Header = () => {
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const handleSubmenuToggle = (index) => {
    setOpenSubmenu(openSubmenu === index ? null : index);
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest('.submenu')) {
      setOpenSubmenu(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLinkClick = (close) => {
    setOpenSubmenu(null);
    close(); // Close the Disclosure panel
  };

  return (
    <header className="sticky top-0 z-[999] w-full transition-colors duration-300 font-bold tracking-wider bg-black/20 font-sans">
      <Disclosure as="nav" className="bg-white">
        {({ open, close }) => (
          <>
            <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
              <div className="relative flex items-center justify-between h-24">
                <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
                <div className="flex-1 flex items-center justify-center sm:items-center sm:justify-start">
                  <div className="flex-shrink-0">
                    <Link href="/">
                      <Image
                        src="/images/logo.png"
                        className="object-cover"
                        alt="logo"
                        width={320}
                        height={69}
                      />
                    </Link>
                  </div>
                  <div className="hidden sm:block sm:ml-6 flex-1 self-center">
                    <div className="flex space-x-14 justify-end">
                      {navigation.map((item, index) =>
                        item.submenu ? (
                          <div key={item.name} className="relative submenu">
                            <button
                              onClick={() => handleSubmenuToggle(index)}
                              className="text-black text-lg px-3 py-2 rounded-md font-medium flex items-center hover:text-color-indigo-600"
                            >
                              {item.name}
                              <ChevronDownIcon
                                className={`ml-2 h-5 w-5 transition-transform ${
                                  openSubmenu === index
                                    ? 'transform rotate-180'
                                    : ''
                                }`}
                              />
                            </button>
                            {openSubmenu === index && (
                              <div className="absolute z-10 bg-white shadow-lg text-left rounded-md py-2 mt-1 w-56">
                                {item.submenu.map((subitem) => (
                                  <Link
                                    key={subitem.name}
                                    href={subitem.href}
                                    className="block px-4 py-2 text-base text-gray-700 hover:bg-gray-100 hover:text-color-indigo-600"
                                    onClick={() => handleLinkClick(close)}
                                  >
                                    {subitem.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link
                            key={item.name}
                            href={item.href}
                            className="text-black px-0 py-2 rounded-md text-lg font-normal hover:text-color-indigo-600"
                            onClick={() => handleLinkClick(close)}
                          >
                            {item.name}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                  {/* <div>
                    <a href="" className=" bg-lime-500 rounded-md text-white py-3 px-6 lg:px-8 ml-6">+91 0000 000000</a>
                  </div> */}
                </div>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigation.map((item, index) =>
                  item.submenu ? (
                    <Disclosure key={item.name} as="div">
                      {({ open: submenuOpen }) => (
                        <>
                          <Disclosure.Button
                            as="button"
                            className="text-black px-3 py-2 rounded-md text-sm font-medium w-full flex items-center justify-between"
                            onClick={() => handleSubmenuToggle(index)}
                          >
                            {item.name}
                            <ChevronDownIcon
                              className={`h-5 w-5 transition-transform ${
                                openSubmenu === index
                                  ? 'transform rotate-180'
                                  : ''
                              }`}
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel
                            className={`space-y-1 ${
                              submenuOpen ? 'block' : 'hidden'
                            }`}
                          >
                            {item.submenu.map((subitem) => (
                              <Link
                                key={subitem.name}
                                href={subitem.href}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                onClick={() => handleLinkClick(close)}
                              >
                                {subitem.name}
                              </Link>
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ) : (
                    <Link
                      key={item.name}
                      href={item.href}
                      className="block text-black px-3 py-2 rounded-md text-sm font-medium"
                      onClick={() => handleLinkClick(close)}
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </header>
  );
};

export default Header;
